/**
 * SleepRecoveryTwinPage — iter612
 * Manual sleep log (bedtime/wake/quality/awakenings/HRV) → Recovery Score 0-100.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Moon, Save } from "lucide-react";
import { toast } from "sonner";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function SleepRecoveryTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    sleep_date: todayISO(),
    bedtime: "23:00",
    wake_time: "07:00",
    quality_1_10: 7,
    awakenings: 0,
    hrv_ms: "",
    rhr_bpm: "",
    note_bg: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/sleep-twin/insights`, {}, { timeout: 30000 });
      setSnapshot(r.data.snapshot);
      setInsight(r.data.insight_bg || "");
    } catch (e) {
      try {
        const { data } = await axios.get(`${API}/sleep-twin/snapshot`);
        setSnapshot(data);
      } catch {}
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sleep_date: form.sleep_date,
        bedtime: form.bedtime,
        wake_time: form.wake_time,
        quality_1_10: parseInt(form.quality_1_10),
        awakenings: parseInt(form.awakenings),
        hrv_ms: form.hrv_ms ? parseFloat(form.hrv_ms) : null,
        rhr_bpm: form.rhr_bpm ? parseFloat(form.rhr_bpm) : null,
        note_bg: form.note_bg || null,
      };
      await axios.post(`${API}/sleep-twin/log`, payload);
      toast.success("Сън записан");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    }
    setSaving(false);
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const curr = snapshot?.current;
  const rolling = snapshot?.rolling_7d;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="sleep-twin-page">
      <MobileTopBar title="Сън" fallbackTo="/dashboard" testid="sleep-twin-mobile-topbar" />
      <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
    </div>;
  }

  const recovery = curr?.recovery_score;
  const recoveryColor = recovery >= 75 ? "#10B981" : recovery >= 50 ? "#F59E0B" : "#EF4444";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="sleep-twin-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="sleep" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Moon className="w-5 h-5 text-indigo-400" />
          <h1 className="text-xl font-bold">😴 {twinLabel("sleep")}</h1>
        </div>

        {curr && (
          <div className={`p-4 rounded-2xl border mb-4 ${cardCls}`} data-testid="sleep-snapshot">
            <div className="text-[10px] uppercase opacity-50 mb-2">Снощи · {curr.sleep_date}</div>
            <div className="flex items-baseline gap-2">
              <div className="text-5xl font-bold" style={{ color: recoveryColor }} data-testid="sleep-recovery-value">
                {recovery ?? "—"}
              </div>
              <div className="text-sm opacity-50">/100 Recovery</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              <SmallStat label="Сън" value={curr.duration_hours} unit="h" testid="sleep-stat-duration" />
              <SmallStat label="Качество" value={curr.quality_1_10} unit="/10" testid="sleep-stat-quality" />
              <SmallStat label="Събуж." value={curr.awakenings} unit="" testid="sleep-stat-awakenings" />
              <SmallStat label="HRV" value={curr.hrv_ms} unit="ms" testid="sleep-stat-hrv" />
            </div>
            {rolling?.samples > 0 && (
              <div className="text-[10px] opacity-60 mt-2">
                7-дн avg: Recovery <b>{rolling.recovery_avg}</b> · сън <b>{rolling.duration_avg}h</b>
                {rolling.hrv_avg && <> · HRV <b>{rolling.hrv_avg}ms</b></>}
                · {rolling.samples} записа
              </div>
            )}
            {insight && (
              <div className={`mt-3 p-3 rounded-lg ${isDark ? "bg-indigo-900/30" : "bg-indigo-50"}`} data-testid="sleep-insight">
                <div className="text-[10px] uppercase opacity-50 mb-1">✨ AI препоръка</div>
                <p className="text-sm whitespace-pre-wrap">{insight}</p>
              </div>
            )}
          </div>
        )}

        <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="sleep-form">
          <h2 className="font-bold text-sm mb-3">Запиши снощния сън</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Дата (събуждане)</Label>
              <Input type="date" value={form.sleep_date} onChange={(e) => setForm({ ...form, sleep_date: e.target.value })} data-testid="sleep-input-date" />
            </div>
            <div>
              <Label className="text-xs">Лягане (HH:MM)</Label>
              <Input type="time" value={form.bedtime} onChange={(e) => setForm({ ...form, bedtime: e.target.value })} data-testid="sleep-input-bedtime" />
            </div>
            <div>
              <Label className="text-xs">Ставане (HH:MM)</Label>
              <Input type="time" value={form.wake_time} onChange={(e) => setForm({ ...form, wake_time: e.target.value })} data-testid="sleep-input-wake" />
            </div>
            <div>
              <Label className="text-xs">Качество (1-10)</Label>
              <Input type="number" min="1" max="10" value={form.quality_1_10} onChange={(e) => setForm({ ...form, quality_1_10: e.target.value })} data-testid="sleep-input-quality" />
            </div>
            <div>
              <Label className="text-xs">Събуждания</Label>
              <Input type="number" min="0" value={form.awakenings} onChange={(e) => setForm({ ...form, awakenings: e.target.value })} data-testid="sleep-input-awakenings" />
            </div>
            <div>
              <Label className="text-xs">HRV ms (по желание)</Label>
              <Input type="number" step="0.1" value={form.hrv_ms} onChange={(e) => setForm({ ...form, hrv_ms: e.target.value })} data-testid="sleep-input-hrv" />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="w-full mt-4 bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="sleep-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Запиши сън
          </Button>
        </div>
      </div>
    </div>
  );
}

function SmallStat({ label, value, unit, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-xl font-bold">{value ?? "—"}{unit}</div>
      <div className="text-[10px] opacity-60 uppercase">{label}</div>
    </div>
  );
}
